<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { Bar, Doughnut } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import Button from 'dashboard/components-next/button/Button.vue';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale
);

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const sinceDate = ref('');
const untilDate = ref('');
const selectedInboxId = ref('');
const loading = ref(false);
const aiLoading = ref(false);
const reportData = ref(null);
const aiAnalysis = ref(null);
const activeSection = ref('overview');

const inboxes = computed(() => getters['inboxes/getInboxes'].value);

const fetchReport = async () => {
  loading.value = true;
  try {
    const response = await store.dispatch('phoneCentralConfigs/getCallReport', {
      since: sinceDate.value || undefined,
      until: untilDate.value || undefined,
      inboxId: selectedInboxId.value || undefined,
    });
    reportData.value = response;
  } catch (error) {
    useAlert(t('PHONE_CENTRAL.REPORT.ERROR'));
  } finally {
    loading.value = false;
  }
};

const fetchAiAnalysis = async () => {
  aiLoading.value = true;
  try {
    const response = await store.dispatch(
      'phoneCentralConfigs/getCallAiAnalysis',
      {
        since: sinceDate.value || undefined,
        until: untilDate.value || undefined,
        inboxId: selectedInboxId.value || undefined,
      }
    );
    aiAnalysis.value = response;
    activeSection.value = 'ai';
  } catch (error) {
    useAlert(t('PHONE_CENTRAL.REPORT.AI_ERROR'));
  } finally {
    aiLoading.value = false;
  }
};

// Chart data
const agentResponseChart = computed(() => {
  if (!reportData.value) return null;
  return {
    labels: [
      t('PHONE_CENTRAL.REPORT.WITH_AGENT'),
      t('PHONE_CENTRAL.REPORT.WITHOUT_AGENT'),
    ],
    datasets: [
      {
        data: [
          reportData.value.with_agent_response,
          reportData.value.without_agent_response,
        ],
        backgroundColor: ['#16a34a', '#dc2626'],
      },
    ],
  };
});

const callStatusChart = computed(() => {
  if (!reportData.value) return null;
  return {
    labels: [
      t('PHONE_CENTRAL.REPORT.ANSWERED'),
      t('PHONE_CENTRAL.REPORT.UNANSWERED'),
    ],
    datasets: [
      {
        data: [
          reportData.value.answered_count,
          reportData.value.unanswered_count,
        ],
        backgroundColor: ['#2563eb', '#f59e0b'],
      },
    ],
  };
});

const hourlyChart = computed(() => {
  if (!reportData.value) return null;
  const dist = reportData.value.hourly_distribution || [];
  return {
    labels: dist.map(h => `${String(h.hour).padStart(2, '0')}h`),
    datasets: [
      {
        label: t('PHONE_CENTRAL.REPORT.CALLS'),
        data: dist.map(h => h.count),
        backgroundColor: '#3b82f6',
        borderRadius: 4,
      },
    ],
  };
});

const dailyChart = computed(() => {
  if (!reportData.value) return null;
  const dist = reportData.value.daily_distribution || [];
  return {
    labels: dist.map(d => {
      const parts = d.date.split('-');
      return `${parts[2]}/${parts[1]}`;
    }),
    datasets: [
      {
        label: t('PHONE_CENTRAL.REPORT.CALLS'),
        data: dist.map(d => d.count),
        backgroundColor: '#8b5cf6',
        borderRadius: 4,
      },
    ],
  };
});

const topPhonesChart = computed(() => {
  if (!reportData.value) return null;
  const phones = (reportData.value.phone_stats || []).slice(0, 15);
  return {
    labels: phones.map(p => p.contact_name || p.phone.replace('+55', '')),
    datasets: [
      {
        label: t('PHONE_CENTRAL.REPORT.ANSWERED'),
        data: phones.map(p => p.answered),
        backgroundColor: '#16a34a',
        borderRadius: 4,
      },
      {
        label: t('PHONE_CENTRAL.REPORT.UNANSWERED'),
        data: phones.map(p => p.unanswered),
        backgroundColor: '#dc2626',
        borderRadius: 4,
      },
    ],
  };
});

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
};

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
};

const stackedBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
  scales: {
    x: { stacked: true },
    y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1 } },
  },
};

const formatPeakHours = hours => {
  if (!hours || typeof hours !== 'object') return '-';
  const entries = Object.entries(hours).sort(
    (a, b) => Number(b[1]) - Number(a[1])
  );
  return entries
    .slice(0, 3)
    .map(([h, c]) => `${String(h).padStart(2, '0')}h (${c}x)`)
    .join(', ');
};

onBeforeMount(() => {
  store.dispatch('inboxes/get');
  fetchReport();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Filters -->
    <div
      class="flex flex-wrap items-end gap-4 p-4 rounded-lg bg-n-background border border-n-weak"
    >
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.SINCE') }}
        </label>
        <input
          v-model="sinceDate"
          type="date"
          class="h-8 px-2 text-sm border rounded border-n-weak bg-n-background"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.UNTIL') }}
        </label>
        <input
          v-model="untilDate"
          type="date"
          class="h-8 px-2 text-sm border rounded border-n-weak bg-n-background"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.INBOX') }}
        </label>
        <select
          v-model="selectedInboxId"
          class="h-8 px-2 text-sm border rounded border-n-weak bg-n-background mb-0"
        >
          <option value="">
            {{ $t('PHONE_CENTRAL.CALL_LOGS.FILTER.ALL_INBOXES') }}
          </option>
          <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
            {{ inbox.name }}
          </option>
        </select>
      </div>
      <Button
        :label="$t('PHONE_CENTRAL.CALL_LOGS.FILTER.APPLY')"
        size="sm"
        @click="fetchReport"
      />
      <Button
        :label="$t('PHONE_CENTRAL.REPORT.AI_ANALYZE')"
        size="sm"
        icon="i-lucide-sparkles"
        :is-loading="aiLoading"
        @click="fetchAiAnalysis"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <span class="text-n-slate-11">
        {{ $t('PHONE_CENTRAL.REPORT.LOADING') }}
      </span>
    </div>

    <template v-else-if="reportData">
      <!-- KPI Cards -->
      <div class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="text-2xl font-bold text-n-slate-12">
            {{ reportData.total_calls }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ $t('PHONE_CENTRAL.REPORT.TOTAL_CALLS') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="text-2xl font-bold text-n-jade-11">
            {{ reportData.answered_count }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ $t('PHONE_CENTRAL.REPORT.ANSWERED') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="text-2xl font-bold text-n-amber-11">
            {{ reportData.unanswered_count }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ $t('PHONE_CENTRAL.REPORT.UNANSWERED') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="text-2xl font-bold text-n-slate-12">
            {{ reportData.total_conversations }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ $t('PHONE_CENTRAL.REPORT.TOTAL_CONVERSATIONS') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="text-2xl font-bold text-n-jade-11">
            {{ reportData.with_agent_response }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ $t('PHONE_CENTRAL.REPORT.WITH_AGENT') }}
          </div>
        </div>
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <div class="text-2xl font-bold text-n-ruby-11">
            {{ reportData.without_agent_response }}
          </div>
          <div class="text-xs text-n-slate-11">
            {{ $t('PHONE_CENTRAL.REPORT.WITHOUT_AGENT') }}
          </div>
        </div>
      </div>

      <!-- Section tabs -->
      <div class="flex gap-2 border-b border-n-weak">
        <button
          class="px-3 py-2 text-xs font-medium border-b-2 transition-colors"
          :class="
            activeSection === 'overview'
              ? 'border-n-brand-9 text-n-brand-11'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeSection = 'overview'"
        >
          {{ $t('PHONE_CENTRAL.REPORT.TAB_OVERVIEW') }}
        </button>
        <button
          class="px-3 py-2 text-xs font-medium border-b-2 transition-colors"
          :class="
            activeSection === 'phones'
              ? 'border-n-brand-9 text-n-brand-11'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeSection = 'phones'"
        >
          {{ $t('PHONE_CENTRAL.REPORT.TAB_PHONES') }}
        </button>
        <button
          v-if="aiAnalysis"
          class="px-3 py-2 text-xs font-medium border-b-2 transition-colors"
          :class="
            activeSection === 'ai'
              ? 'border-n-brand-9 text-n-brand-11'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
          "
          @click="activeSection = 'ai'"
        >
          {{ $t('PHONE_CENTRAL.REPORT.TAB_AI') }}
        </button>
      </div>

      <!-- Overview Section -->
      <div v-if="activeSection === 'overview'" class="flex flex-col gap-6">
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <!-- Agent Response Doughnut -->
          <div class="p-4 rounded-lg border border-n-weak bg-n-background">
            <h3 class="mb-4 text-sm font-medium text-n-slate-12">
              {{ $t('PHONE_CENTRAL.REPORT.AGENT_RESPONSE_CHART') }}
            </h3>
            <div class="h-[250px]">
              <Doughnut
                v-if="agentResponseChart"
                :data="agentResponseChart"
                :options="doughnutOptions"
              />
            </div>
          </div>

          <!-- Call Status Doughnut -->
          <div class="p-4 rounded-lg border border-n-weak bg-n-background">
            <h3 class="mb-4 text-sm font-medium text-n-slate-12">
              {{ $t('PHONE_CENTRAL.REPORT.CALL_STATUS_CHART') }}
            </h3>
            <div class="h-[250px]">
              <Doughnut
                v-if="callStatusChart"
                :data="callStatusChart"
                :options="doughnutOptions"
              />
            </div>
          </div>
        </div>

        <!-- Hourly Distribution -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ $t('PHONE_CENTRAL.REPORT.HOURLY_CHART') }}
          </h3>
          <div class="h-[250px]">
            <Bar v-if="hourlyChart" :data="hourlyChart" :options="barOptions" />
          </div>
        </div>

        <!-- Daily Distribution -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ $t('PHONE_CENTRAL.REPORT.DAILY_CHART') }}
          </h3>
          <div class="h-[250px]">
            <Bar v-if="dailyChart" :data="dailyChart" :options="barOptions" />
          </div>
        </div>
      </div>

      <!-- Phones Section -->
      <div v-if="activeSection === 'phones'" class="flex flex-col gap-6">
        <!-- Top Phones Chart -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ $t('PHONE_CENTRAL.REPORT.TOP_PHONES_CHART') }}
          </h3>
          <div class="h-[350px]">
            <Bar
              v-if="topPhonesChart"
              :data="topPhonesChart"
              :options="stackedBarOptions"
            />
          </div>
        </div>

        <!-- Phone Stats Table -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <h3 class="mb-4 text-sm font-medium text-n-slate-12">
            {{ $t('PHONE_CENTRAL.REPORT.PHONE_DETAILS') }}
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-n-weak">
                  <th class="p-2 text-left text-n-slate-11">
                    {{ $t('PHONE_CENTRAL.REPORT.TABLE.PHONE') }}
                  </th>
                  <th class="p-2 text-left text-n-slate-11">
                    {{ $t('PHONE_CENTRAL.REPORT.TABLE.CONTACT') }}
                  </th>
                  <th class="p-2 text-center text-n-slate-11">
                    {{ $t('PHONE_CENTRAL.REPORT.TABLE.TOTAL') }}
                  </th>
                  <th class="p-2 text-center text-n-slate-11">
                    {{ $t('PHONE_CENTRAL.REPORT.TABLE.ANSWERED_SHORT') }}
                  </th>
                  <th class="p-2 text-center text-n-slate-11">
                    {{ $t('PHONE_CENTRAL.REPORT.TABLE.UNANSWERED_SHORT') }}
                  </th>
                  <th class="p-2 text-left text-n-slate-11">
                    {{ $t('PHONE_CENTRAL.REPORT.TABLE.PEAK_HOURS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="phone in reportData.phone_stats"
                  :key="phone.phone"
                  class="border-b border-n-weak hover:bg-n-slate-2"
                >
                  <td class="p-2 font-medium text-n-slate-12">
                    {{ phone.phone }}
                  </td>
                  <td class="p-2 text-n-slate-11">
                    {{ phone.contact_name || '-' }}
                  </td>
                  <td class="p-2 text-center font-medium text-n-slate-12">
                    {{ phone.total }}
                  </td>
                  <td class="p-2 text-center text-n-jade-11">
                    {{ phone.answered }}
                  </td>
                  <td class="p-2 text-center text-n-ruby-11">
                    {{ phone.unanswered }}
                  </td>
                  <td class="p-2 text-n-slate-11">
                    {{ formatPeakHours(phone.hours) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- AI Analysis Section -->
      <div
        v-if="activeSection === 'ai' && aiAnalysis"
        class="flex flex-col gap-6"
      >
        <!-- AI Summary -->
        <div class="p-4 rounded-lg border border-n-weak bg-n-background">
          <h3
            class="mb-3 text-sm font-medium text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-sparkles text-n-brand-9" />
            {{ $t('PHONE_CENTRAL.REPORT.AI_SUMMARY') }}
          </h3>
          <p
            v-if="aiAnalysis.resumo"
            class="text-sm text-n-slate-11 leading-relaxed"
          >
            {{ aiAnalysis.resumo.observacao_geral }}
          </p>
          <div v-if="aiAnalysis.resumo" class="grid grid-cols-3 gap-4 mt-4">
            <div class="text-center">
              <div class="text-lg font-bold text-n-slate-12">
                {{ aiAnalysis.resumo.taxa_resposta_agente }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ $t('PHONE_CENTRAL.REPORT.AI_AGENT_RATE') }}
              </div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-n-amber-11">
                {{ aiAnalysis.resumo.total_nao_atendidas }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ $t('PHONE_CENTRAL.REPORT.UNANSWERED') }}
              </div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-n-ruby-11">
                {{ aiAnalysis.resumo.total_sem_resposta_agente }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ $t('PHONE_CENTRAL.REPORT.WITHOUT_AGENT') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Unanswered Calls Analysis -->
        <div
          v-if="aiAnalysis.ligacoes_nao_atendidas?.detalhes?.length"
          class="p-4 rounded-lg border border-n-weak bg-n-background"
        >
          <h3 class="mb-3 text-sm font-medium text-n-ruby-11">
            {{ $t('PHONE_CENTRAL.REPORT.AI_UNANSWERED_TITLE') }}
            ({{ aiAnalysis.ligacoes_nao_atendidas.total }})
          </h3>
          <p class="text-xs text-n-slate-11 mb-3 leading-relaxed">
            {{ aiAnalysis.ligacoes_nao_atendidas.analise }}
          </p>
          <div class="space-y-2">
            <div
              v-for="(item, idx) in aiAnalysis.ligacoes_nao_atendidas.detalhes"
              :key="idx"
              class="flex items-start gap-3 p-2 rounded bg-n-ruby-1 border border-n-ruby-3"
            >
              <div class="flex-1">
                <div class="text-xs font-medium text-n-slate-12">
                  {{ item.telefone }} - {{ item.contato || 'Desconhecido' }}
                </div>
                <div class="text-xs text-n-slate-11">
                  {{ item.data_hora }}
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                  <span v-if="item.teve_retorno" class="text-n-jade-11 ml-2">
                    (retornado)
                  </span>
                </div>
                <div
                  v-if="item.observacao"
                  class="text-xs text-n-slate-10 mt-1"
                >
                  {{ item.observacao }}
                </div>
              </div>
              <a
                v-if="item.conversation_id"
                :href="`/app/accounts/${$route.params.accountId}/conversations/${item.conversation_id}`"
                class="text-xs text-n-brand-11 hover:underline whitespace-nowrap"
              >
                #{{ item.conversation_id }}
              </a>
            </div>
          </div>
        </div>

        <!-- Conversations Without Agent -->
        <div
          v-if="aiAnalysis.conversas_sem_agente?.detalhes?.length"
          class="p-4 rounded-lg border border-n-weak bg-n-background"
        >
          <h3 class="mb-3 text-sm font-medium text-n-amber-11">
            {{ $t('PHONE_CENTRAL.REPORT.AI_NO_AGENT_TITLE') }}
            ({{ aiAnalysis.conversas_sem_agente.total }})
          </h3>
          <p class="text-xs text-n-slate-11 mb-3 leading-relaxed">
            {{ aiAnalysis.conversas_sem_agente.analise }}
          </p>
          <div class="space-y-2">
            <div
              v-for="(item, idx) in aiAnalysis.conversas_sem_agente.detalhes"
              :key="idx"
              class="flex items-start gap-3 p-2 rounded bg-n-amber-1 border border-n-amber-3"
            >
              <div class="flex-1">
                <div class="text-xs font-medium text-n-slate-12">
                  {{ item.telefone }} - {{ item.contato || 'Desconhecido' }}
                </div>
                <div v-if="item.notas_privadas?.length" class="mt-1 space-y-1">
                  <!-- eslint-disable vue/no-bare-strings-in-template -->
                  <div
                    v-for="(nota, nIdx) in item.notas_privadas"
                    :key="nIdx"
                    class="text-xs text-n-slate-10 italic"
                  >
                    &ldquo;{{ nota }}&rdquo;
                  </div>
                  <!-- eslint-enable vue/no-bare-strings-in-template -->
                </div>
                <div
                  v-if="item.observacao"
                  class="text-xs text-n-slate-10 mt-1"
                >
                  {{ item.observacao }}
                </div>
              </div>
              <a
                v-if="item.conversation_id"
                :href="`/app/accounts/${$route.params.accountId}/conversations/${item.conversation_id}`"
                class="text-xs text-n-brand-11 hover:underline whitespace-nowrap"
              >
                #{{ item.conversation_id }}
              </a>
            </div>
          </div>
        </div>

        <!-- Private Notes Found -->
        <div
          v-if="aiAnalysis.notas_privadas_encontradas?.length"
          class="p-4 rounded-lg border border-n-weak bg-n-background"
        >
          <h3 class="mb-3 text-sm font-medium text-n-slate-12">
            {{ $t('PHONE_CENTRAL.REPORT.AI_PRIVATE_NOTES') }}
            ({{ aiAnalysis.notas_privadas_encontradas.length }})
          </h3>
          <div class="space-y-2">
            <div
              v-for="(nota, idx) in aiAnalysis.notas_privadas_encontradas"
              :key="idx"
              class="flex items-start gap-3 p-2 rounded bg-n-slate-2 border border-n-weak"
            >
              <div class="flex-1">
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                <div class="text-xs font-medium text-n-slate-12">
                  {{ nota.telefone }} - {{ nota.autor }}
                </div>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <div class="text-xs text-n-slate-11 italic mt-1">
                  &ldquo;{{ nota.conteudo }}&rdquo;
                </div>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
                <div class="text-xs text-n-slate-10 mt-1">{{ nota.data }}</div>
              </div>
              <a
                v-if="nota.conversation_id"
                :href="`/app/accounts/${$route.params.accountId}/conversations/${nota.conversation_id}`"
                class="text-xs text-n-brand-11 hover:underline whitespace-nowrap"
              >
                #{{ nota.conversation_id }}
              </a>
            </div>
          </div>
        </div>

        <!-- Frequent Clients -->
        <div
          v-if="aiAnalysis.clientes_frequentes?.length"
          class="p-4 rounded-lg border border-n-weak bg-n-background"
        >
          <h3 class="mb-3 text-sm font-medium text-n-slate-12">
            {{ $t('PHONE_CENTRAL.REPORT.AI_FREQUENT_CLIENTS') }}
          </h3>
          <div class="space-y-2">
            <div
              v-for="(client, idx) in aiAnalysis.clientes_frequentes"
              :key="idx"
              class="p-2 rounded bg-n-slate-2 border border-n-weak"
            >
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-n-slate-12">
                  {{ client.telefone }}
                </span>
                <span v-if="client.contato" class="text-xs text-n-slate-11">
                  ({{ client.contato }})
                </span>
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <span
                  class="ml-auto text-xs font-medium px-2 py-0.5 rounded bg-n-brand-2 text-n-brand-11"
                >
                  {{ client.total_ligacoes }}x
                </span>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
              </div>
              <div class="text-xs text-n-slate-10 mt-1">
                {{ client.analise }}
              </div>
            </div>
          </div>
        </div>

        <!-- Recommendations -->
        <div
          v-if="aiAnalysis.recomendacoes?.length"
          class="p-4 rounded-lg border border-n-jade-3 bg-n-jade-1"
        >
          <h3 class="mb-3 text-sm font-medium text-n-jade-11">
            {{ $t('PHONE_CENTRAL.REPORT.AI_RECOMMENDATIONS') }}
          </h3>
          <ul class="space-y-2">
            <li
              v-for="(rec, idx) in aiAnalysis.recomendacoes"
              :key="idx"
              class="flex items-start gap-2 text-xs text-n-slate-12"
            >
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              <span class="text-n-jade-11 mt-0.5 shrink-0">&#10003;</span>
              {{ rec }}
            </li>
          </ul>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else class="flex items-center justify-center py-12 text-n-slate-11">
      {{ $t('PHONE_CENTRAL.REPORT.EMPTY') }}
    </div>
  </div>
</template>
